import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays } from "lucide-react";
import { HERO_SLIDES } from "@/data/homeData";

export function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
      {HERO_SLIDES.map((slide, index) => (
        <div
          key={index}
          className="absolute inset-0 transition-opacity duration-[1800ms] ease-in-out"
          style={{ opacity: currentSlide === index ? 1 : 0 }}
        >
          <img
            src={slide.image}
            alt={slide.subtitle}
            className="w-full h-full object-cover"
            style={{ animation: currentSlide === index ? "heroKenBurns 8s ease-out forwards" : "none" }}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#004333]/90 via-[#004333]/70 to-[#004333]/30" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <div className="max-w-3xl">
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-[#C9922A] uppercase tracking-[0.25em] text-xs font-semibold mb-5">
            Conciergerie & Sous-location professionnelle
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-4xl md:text-5xl lg:text-[3.4rem] text-white leading-[1.15] mb-6">
            Votre patrimoine mérite la <span className="text-[#C9922A]">sérénité</span>.{" "}
            <br className="hidden md:block" />
            Votre rentabilité mérite l'<span className="text-[#C9922A]">expertise</span>.
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="text-base text-white/70 mb-8 leading-relaxed max-w-2xl">
            Chez Happy House, nous transformons la gestion locative en une expérience 
            sans stress pour les propriétaires de <strong className="text-white/90">Limoges</strong> et 
            d'<strong className="text-white/90">Évaux-les-Bains</strong>. Que vous cherchiez un loyer garanti 
            ou une gestion en mandat de prestation dynamique, nous donnons vie à vos projets immobiliers.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }} className="grid sm:grid-cols-3 gap-4 mb-10">
            {[
              { num: "01", title: "Loyer Garanti", desc: "Encaissez votre loyer chaque mois, sans exception." },
              { num: "02", title: "Gestion 5 Étoiles", desc: "Un entretien hôtelier pour un patrimoine valorisé." },
              { num: "03", title: "Proximité Réelle", desc: "Une équipe locale, réactive et passionnée." },
            ].map((pillar, i) => (
              <div key={i} className="bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/[0.1] hover:border-[#C9922A]/20 transition-all duration-300">
                <span className="text-[#C9922A] font-mono text-xs font-bold">{pillar.num}</span>
                <h3 className="text-white font-semibold text-sm mt-1 mb-1.5">{pillar.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="flex flex-col sm:flex-row gap-4">
            <Link to="/proprietaires#contact-form" className="btn-hero-outline inline-flex items-center justify-center gap-2.5" data-testid="hero-cta-proprietaire">
              Confier mon bien <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/voyageurs" className="btn-light inline-flex items-center justify-center gap-2.5" data-testid="hero-cta-reserver">
              <CalendarDays className="w-4 h-4" /> Réserver un logement
            </Link>
          </motion.div>

          <div className="flex items-center gap-3 mt-12">
            {HERO_SLIDES.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className="relative h-[3px] overflow-hidden rounded-full transition-all duration-500"
                style={{ width: currentSlide === index ? 48 : 24, backgroundColor: currentSlide === index ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.15)" }}
                data-testid={`hero-slide-indicator-${index}`}
              >
                {currentSlide === index && (
                  <motion.div className="absolute inset-y-0 left-0 bg-white rounded-full" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 6, ease: "linear" }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
